import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Trash2, Plus, Minus, ShoppingCart, ArrowLeft, ArrowRight, Package } from 'lucide-react';
import { useAppContext } from '../context/AppContext';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
export function Cart() {
  const {
    cart,
    removeFromCart,
    updateCartQuantity,
    clearCart,
    cartTotal
  } = useAppContext();
  const getCategoryLabel = (category: string) => {
    switch (category) {
      case 'hosting':
        return 'Web Hosting';
      case 'radio':
        return 'Radio Station';
      case 'reseller':
        return 'Reseller';
      case 'shoutcast':
        return 'Shoutcast';
      case 'shoutcast-reseller':
        return 'Shoutcast Reseller';
      default:
        return category;
    }
  };
  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'hosting':
        return 'bg-fox-500/10 text-fox-500';
      case 'radio':
        return 'bg-amber-500/10 text-amber-500';
      case 'reseller':
        return 'bg-fox-500/10 text-fox-500';
      case 'shoutcast':
        return 'bg-purple-500/10 text-purple-500';
      case 'shoutcast-reseller':
        return 'bg-cyan-500/10 text-cyan-500';
      default:
        return 'bg-slate-800 text-slate-400';
    }
  };
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="min-h-screen bg-slate-950" data-id="element-1551">
      <Navbar data-id="element-1552" />
      <main className="pt-32 pb-24" data-id="element-1553">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl" data-id="element-1554">
          <div className="flex items-center justify-between mb-10" data-id="element-1555">
            <div data-id="element-1556">
              <h1 className="text-3xl md:text-4xl font-heading font-bold text-white mb-2 flex items-center gap-3" data-id="element-1557">
                <ShoppingCart className="text-fox-500" size={32} data-id="element-1558" />
                Shopping Cart
              </h1>
              <p className="text-slate-400" data-id="element-1559">
                {cart.length === 0 ? 'Your cart is empty.' : `${cart.reduce((s, i) => s + i.quantity, 0)} item${cart.reduce((s, i) => s + i.quantity, 0) !== 1 ? 's' : ''} in your cart`}
              </p>
            </div>
            {cart.length > 0 && <button onClick={clearCart} className="text-sm text-slate-400 hover:text-red-400 transition-colors" data-id="element-1560">
                Clear Cart
              </button>}
          </div>

          {cart.length === 0 ? <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="text-center py-20 bg-slate-900 border border-slate-800 rounded-2xl" data-id="element-1561">
              <Package size={64} className="text-slate-700 mx-auto mb-6" data-id="element-1562" />
              <h2 className="text-2xl font-heading font-bold text-white mb-3" data-id="element-1563">
                Nothing here yet
              </h2>
              <p className="text-slate-400 mb-8 max-w-md mx-auto" data-id="element-1564">
                Browse our pricing plans and add services to your cart to get
                started.
              </p>
              <Link to="/#pricing" className="inline-flex items-center gap-2 bg-fox-600 hover:bg-fox-500 text-white px-6 py-3 rounded-xl font-medium transition-colors" data-id="element-1565">
                <ArrowLeft size={18} data-id="element-1566" />
                Browse Plans
              </Link>
            </motion.div> : <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-1567">
              {/* Cart Items */}
              <div className="lg:col-span-2 space-y-4" data-id="element-1568">
                {cart.map((item, i) => <motion.div key={`${item.packageId}-${item.billing}`} initial={{
              opacity: 0,
              x: -20
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: i * 0.05
            }} className="bg-slate-900 border border-slate-800 rounded-2xl p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-1569">
                    <div className="flex-1" data-id="element-1570">
                      <div className="flex items-center gap-2 mb-2" data-id="element-1571">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${getCategoryColor(item.category)}`} data-id="element-1572">
                          {getCategoryLabel(item.category)}
                        </span>
                        <span className="text-xs text-slate-500 bg-slate-800 px-2 py-0.5 rounded-full" data-id="element-1573">
                          {item.billing}
                        </span>
                      </div>
                      <h3 className="text-lg font-bold text-white" data-id="element-1574">
                        {item.name}
                      </h3>
                      <p className="text-slate-400 text-sm" data-id="element-1575">
                        ${item.price.toFixed(2)}/
                        {item.billing === 'monthly' ? 'mo' : 'yr'}
                      </p>
                    </div>

                    <div className="flex items-center gap-4" data-id="element-1576">
                      {/* Quantity Controls */}
                      <div className="flex items-center gap-2 bg-slate-950 border border-slate-800 rounded-lg" data-id="element-1577">
                        <button onClick={() => updateCartQuantity(item.packageId, item.quantity - 1)} className="p-2 text-slate-400 hover:text-white transition-colors" data-id="element-1578">
                          <Minus size={14} data-id="element-1579" />
                        </button>
                        <span className="text-white font-medium w-6 text-center" data-id="element-1580">
                          {item.quantity}
                        </span>
                        <button onClick={() => updateCartQuantity(item.packageId, item.quantity + 1)} className="p-2 text-slate-400 hover:text-white transition-colors" data-id="element-1581">
                          <Plus size={14} data-id="element-1582" />
                        </button>
                      </div>

                      {/* Line Total */}
                      <span className="text-white font-bold w-20 text-right" data-id="element-1583">
                        ${(item.price * item.quantity).toFixed(2)}
                      </span>

                      {/* Remove */}
                      <button onClick={() => removeFromCart(item.packageId)} className="p-2 text-slate-500 hover:text-red-500 transition-colors" data-id="element-1584">
                        <Trash2 size={18} data-id="element-1585" />
                      </button>
                    </div>
                  </motion.div>)}
              </div>

              {/* Order Summary */}
              <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.2
          }} className="lg:col-span-1" data-id="element-1586">
                <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 sticky top-28" data-id="element-1587">
                  <h2 className="text-xl font-heading font-bold text-white mb-6" data-id="element-1588">
                    Order Summary
                  </h2>

                  <div className="space-y-4 mb-6" data-id="element-1589">
                    {cart.map(item => <div key={`${item.packageId}-${item.billing}`} className="flex justify-between text-sm" data-id="element-1590">
                        <span className="text-slate-400 truncate mr-2" data-id="element-1591">
                          {item.name} x{item.quantity}
                        </span>
                        <span className="text-white font-medium shrink-0" data-id="element-1592">
                          ${(item.price * item.quantity).toFixed(2)}
                        </span>
                      </div>)}
                  </div>

                  <div className="border-t border-slate-800 pt-4 mb-6" data-id="element-1593">
                    <div className="flex justify-between mb-2" data-id="element-1594">
                      <span className="text-slate-400" data-id="element-1595">Subtotal</span>
                      <span className="text-white font-medium" data-id="element-1596">
                        ${cartTotal.toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between mb-2" data-id="element-1597">
                      <span className="text-slate-400" data-id="element-1598">Tax</span>
                      <span className="text-slate-500 text-sm" data-id="element-1599">
                        Calculated at checkout
                      </span>
                    </div>
                  </div>

                  <div className="border-t border-slate-800 pt-4 mb-6" data-id="element-1600">
                    <div className="flex justify-between" data-id="element-1601">
                      <span className="text-white font-bold text-lg" data-id="element-1602">
                        Total
                      </span>
                      <span className="text-white font-bold text-lg" data-id="element-1603">
                        ${cartTotal.toFixed(2)}
                      </span>
                    </div>
                  </div>

                  <Link to="/checkout" className="w-full bg-fox-600 hover:bg-fox-500 text-white font-medium py-3 rounded-xl transition-all flex items-center justify-center gap-2 shadow-[0_0_15px_rgba(234,88,12,0.2)] hover:shadow-[0_0_25px_rgba(234,88,12,0.4)]" data-id="element-1604">
                    Proceed to Checkout <ArrowRight size={18} data-id="element-1605" />
                  </Link>

                  <Link to="/#pricing" className="w-full mt-3 py-3 rounded-xl font-medium text-slate-400 hover:text-white transition-colors flex items-center justify-center gap-2 text-sm" data-id="element-1606">
                    <ArrowLeft size={16} data-id="element-1607" />
                    Continue Shopping
                  </Link>
                </div>
              </motion.div>
            </div>}
        </div>
      </main>
      <Footer data-id="element-1608" />
    </motion.div>;
}